"use client";

import LanguageSwitcher from "@/components/shared/LanguageToggle/LanguageToggle";
import { useAuthUser } from "@/hooks/auth.hook";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import type { NavLink } from "./useHeader";

interface MobileMenuProps {
  isOpen: boolean;
  navLinks: NavLink[];
  locale: string;
  loginLabel: string;
  listLabel: string;
  onClose: () => void;
  // Login is a dialog (there's no /auth/login page) — same one as on desktop.
  onLoginClick: () => void;
}

export function MobileMenu({
  isOpen,
  navLinks,
  locale,
  loginLabel,
  listLabel,
  onClose,
  onLoginClick,
}: MobileMenuProps) {
  const isRTL = locale === "ar";
  const { data: user } = useAuthUser();
  const t = useTranslations("auth");

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, x: isRTL ? "100%" : "-100%" }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: isRTL ? "100%" : "-100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed inset-0 z-100 bg-white dark:bg-black flex flex-col p-8 lg:hidden"
        >
          {/* Close Header */}
          <div className="flex items-center justify-between mb-12">
            <span className="font-serif text-xl font-bold text-brand-primary tracking-tighter">
              DIRIYAH HOMES
            </span>
            <button
              onClick={onClose}
              className="p-2 bg-gray-50 dark:bg-white/10 rounded-full text-brand-dark dark:text-white"
            >
              <X size={24} />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="flex flex-col gap-8 mb-auto">
            {navLinks.map((link, idx) => (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
                key={link.key}
              >
                <Link
                  href={link.href}
                  onClick={onClose}
                  className="text-4xl font-serif font-medium text-brand-dark dark:text-white active:text-brand-primary"
                >
                  {link.label}
                </Link>
              </motion.div>
            ))}
          </nav>

          {/* Bottom Section */}
          <div className="space-y-6 pt-8 border-t border-gray-100 dark:border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-white/40">
                Language
              </span>
              <LanguageSwitcher variant="scrolled" />
            </div>

            <div className="grid grid-cols-1 gap-4">
              {user ? (
                <Link
                  href={`/${locale}/profile/my-profile`}
                  onClick={onClose}
                  className="w-full py-4 rounded-xl border border-gray-200 dark:border-white/20 text-center font-bold uppercase text-[12px] tracking-widest text-brand-dark dark:text-white"
                >
                  {t("myProfile")}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onLoginClick();
                  }}
                  className="w-full py-4 rounded-xl border border-gray-200 dark:border-white/20 text-center font-bold uppercase text-[12px] tracking-widest text-brand-dark dark:text-white"
                >
                  {loginLabel}
                </button>
              )}
              <Link
                href={`/${locale}/list-property`}
                onClick={onClose}
                className="w-full py-4 rounded-xl bg-brand-primary text-center font-bold uppercase text-[12px] tracking-widest text-white shadow-lg shadow-brand-primary/20"
              >
                {listLabel}
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
